import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import { createUseStyles } from "react-jss";
import type { Boat, BoatInput, DamagePolicy } from "../../../../common/src";
import { DAMAGE_POLICY_LABELS, boatInputSchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import { trpc } from "../../trpc";
import { toFieldErrors } from "../../utils/fieldErrors";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { LoadError, Loading } from "../shared/StatusViews";
import { BoatPhotoCard } from "./BoatPhotoCard";
import { ChecklistEditor } from "./ChecklistEditor";
import { navigate } from "../../hooks/useRoute";
import { routeToHash } from "../../routes";

const usePageStyles = createUseStyles({
  back: {
    display: "inline-block",
    marginBottom: 14,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
});
import { useFormStyles, useLayoutStyles } from "../shared/styles";

type TextKey =
  | "name"
  | "makeModel"
  | "lengthFeet"
  | "storageLocation"
  | "ownerName"
  | "description"
  | "rules"
  | "capacity"
  | "maxConsecutiveDays"
  | "minAdvanceDays"
  | "maxAdvanceDays";

type FormValues = Record<TextKey, string> & {
  isActive: boolean;
  damagePolicy: DamagePolicy;
};

const NEW_BOAT: FormValues = {
  name: "",
  makeModel: "",
  lengthFeet: "",
  storageLocation: "",
  ownerName: "",
  description: "",
  rules: "",
  capacity: "6",
  maxConsecutiveDays: "3",
  minAdvanceDays: "1",
  maxAdvanceDays: "90",
  isActive: true,
  damagePolicy: "ownerWaives",
};

const toFormValues = (boat: Boat): FormValues => ({
  name: boat.name,
  makeModel: boat.makeModel ?? "",
  lengthFeet: boat.lengthFeet?.toString() ?? "",
  storageLocation: boat.storageLocation ?? "",
  ownerName: boat.ownerName,
  damagePolicy: boat.damagePolicy,
  description: boat.description ?? "",
  rules: boat.rules,
  capacity: String(boat.capacity),
  maxConsecutiveDays: String(boat.maxConsecutiveDays),
  minAdvanceDays: String(boat.minAdvanceDays),
  maxAdvanceDays: String(boat.maxAdvanceDays),
  isActive: boat.isActive,
});

const DAMAGE_POLICIES: readonly DamagePolicy[] = [
  "ownerWaives",
  "borrowerPays",
];

const DAMAGE_POLICY_HINTS: Record<DamagePolicy, string> = {
  ownerWaives:
    "You accept the risk of damage to the boat and won't ask the borrower to pay, except for drunk, reckless, illegal, or unapproved use.",
  borrowerPays:
    "The borrower pays reasonable repair costs, less anything your insurer covers.",
};

const toNumber = (value: string): number =>
  value.trim() === "" ? Number.NaN : Number(value);

const parseForm = (v: FormValues) =>
  boatInputSchema.safeParse({
    ...v,
    lengthFeet: v.lengthFeet.trim() === "" ? undefined : toNumber(v.lengthFeet),
    capacity: toNumber(v.capacity),
    maxConsecutiveDays: toNumber(v.maxConsecutiveDays),
    minAdvanceDays: toNumber(v.minAdvanceDays),
    maxAdvanceDays: toNumber(v.maxAdvanceDays),
  });

const BoatForm = ({
  boat,
  onSaved,
}: {
  boat: Boat | undefined;
  onSaved: (boat: Boat) => void;
}) => {
  const form = useFormStyles();
  const layout = useLayoutStyles();
  const [values, setValues] = useState<FormValues>(
    boat ? toFormValues(boat) : NEW_BOAT
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const save = useAction(
    useCallback(
      (input: { id: number | undefined; boat: BoatInput }) =>
        trpc.boat.save.mutate(input),
      []
    )
  );

  const set = (key: TextKey) => (e: { target: { value: string } }) => {
    setSaved(false);
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseForm(values);
    if (!parsed.success) {
      setErrors(toFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    const result = await save.run({ id: boat?.id, boat: parsed.data });
    if (result) {
      setSaved(true);
      onSaved(result);
    }
  };

  const text = (key: TextKey, label: string, hint?: string, type = "text") => (
    <Field
      label={label}
      htmlFor={`boat-${key}`}
      hint={hint}
      error={errors[key]}
    >
      <input
        id={`boat-${key}`}
        type={type}
        className={form.control}
        value={values[key]}
        onChange={set(key)}
      />
    </Field>
  );

  return (
    <form onSubmit={(e) => void submit(e)} noValidate>
      <div className={layout.sections}>
        <Card title="Boat details">
          <div className={form.grid}>
            {text("name", "Name")}
            {text("makeModel", "Make and model", "Optional")}
            {text("lengthFeet", "Length (ft)", "Optional", "number")}
            {text("storageLocation", "Storage location", "Optional")}
            {text(
              "ownerName",
              "Owner name",
              "Named as the Owner on every Boat Loan Agreement"
            )}
          </div>
          <div className={form.spaced}>
            <Field
              label="Description"
              htmlFor="boat-description"
              hint="Optional. Shown to veterans."
              error={errors.description}
            >
              <textarea
                id="boat-description"
                className={`${form.control} ${form.textarea}`}
                value={values.description}
                onChange={set("description")}
              />
            </Field>
          </div>
        </Card>
        <Card title="Rules and restrictions">
          <div className={form.grid}>
            {text("capacity", "Capacity (people)", undefined, "number")}
            {text(
              "maxConsecutiveDays",
              "Max consecutive days",
              "Longest single reservation",
              "number"
            )}
            {text(
              "minAdvanceDays",
              "Minimum notice (days)",
              "0 allows same-day bookings",
              "number"
            )}
            {text(
              "maxAdvanceDays",
              "Booking horizon (days)",
              "How far ahead people can book",
              "number"
            )}
          </div>
          <div className={form.spaced}>
            <Field
              label="Rules shown to veterans"
              htmlFor="boat-rules"
              hint="One rule per line, e.g. no smoking, return times, required certifications."
              error={errors.rules}
            >
              <textarea
                id="boat-rules"
                className={`${form.control} ${form.textarea}`}
                rows={6}
                value={values.rules}
                onChange={set("rules")}
              />
            </Field>
          </div>
          <label className={`${form.checkbox} ${form.spaced}`}>
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={(e) => {
                setSaved(false);
                setValues((prev) => ({ ...prev, isActive: e.target.checked }));
              }}
            />
            <span>Accepting reservations</span>
          </label>
        </Card>
        <Card title="Damage to the boat">
          <p className={form.intro}>
            Choose who bears the cost if the boat itself is damaged during a
            loan. It appears on the boat's listing and in every Boat Loan
            Agreement. Harm to anyone or anything else is always the borrower's
            responsibility.
          </p>
          <div className={form.stack}>
            {DAMAGE_POLICIES.map((policy) => (
              <label key={policy} className={form.checkbox}>
                <input
                  type="radio"
                  name="damage-policy"
                  checked={values.damagePolicy === policy}
                  onChange={() => {
                    setSaved(false);
                    setValues((prev) => ({ ...prev, damagePolicy: policy }));
                  }}
                />
                <span>
                  <strong>{DAMAGE_POLICY_LABELS[policy]}</strong>
                  <br />
                  {DAMAGE_POLICY_HINTS[policy]}
                </span>
              </label>
            ))}
          </div>
          <Notice kind="info">
            {
              'Before choosing "Owner waives", check with your insurer that waiving recovery from a borrower won\'t affect your coverage.'
            }
          </Notice>
        </Card>
      </div>
      {save.error && <Notice kind="error">{save.error}</Notice>}
      {Object.keys(errors).length > 0 && (
        <Notice kind="error">Please fix the highlighted fields.</Notice>
      )}
      {saved && <Notice kind="success">Boat saved.</Notice>}
      <div className={form.actions}>
        <Button type="submit" disabled={save.pending}>
          {save.pending ? "Saving…" : boat ? "Save changes" : "Create boat"}
        </Button>
      </div>
    </form>
  );
};

/** Admin editor for one boat, or a blank form when `boatId` is "new". */
export const BoatPage = ({ boatId }: { boatId: number | "new" }) => {
  const layout = useLayoutStyles();
  const classes = usePageStyles();
  const loadBoat = useCallback(
    (signal: AbortSignal) =>
      boatId === "new"
        ? Promise.resolve(undefined)
        : trpc.boat.get.query({ id: boatId }, { signal }),
    [boatId]
  );
  const boat = useAsyncData(loadBoat);
  const [latest, setLatest] = useState<Boat | undefined>(undefined);

  const back = (
    <a className={classes.back} href={routeToHash({ page: "adminBoats" })}>
      ← All boats
    </a>
  );
  if (boat.error) {
    return (
      <div>
        {back}
        <LoadError message={boat.error} onRetry={boat.reload} />
      </div>
    );
  }
  if (boat.loading && !boat.data) {
    return <Loading label="Loading boat…" />;
  }
  const current = latest ?? boat.data;
  return (
    <div>
      {back}
      <div className={layout.sections}>
        {current && <BoatPhotoCard boat={current} onChanged={setLatest} />}
        <BoatForm
          key={current?.id ?? "new"}
          boat={current}
          onSaved={(saved) => {
            setLatest(saved);
            if (boatId === "new") {
              // Move to the new boat's own URL so photo upload becomes available.
              navigate({ page: "adminBoat", boatId: saved.id });
            }
          }}
        />
        {current && <ChecklistEditor boatId={current.id} />}
      </div>
    </div>
  );
};
